import React, { useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ReactCountryFlag } from "react-country-flag";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import { mapiSenderAddressFieldDisplayOrder } from "../../../../../constants/send.constant";

countries.registerLocale(enLocale);

interface MapiReviewFinalStepProps {
  onContinue: () => void;
  onEditStep: (key: string) => void;
  testID?: string | undefined;
}

const MapiReviewFinalStep: React.FC<MapiReviewFinalStepProps> = ({
  onEditStep,
  onContinue,
  testID,
}) => {
  const { t, i18n } = useTranslation();

  const pendingSendOption = useSessionStore(
    (s: SessionState) => (s as any).pendingSendOption
  );
  const selectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryCode
  );
  const selectedDestinationCountryName = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryName
  );
  const selectedDestinationStateAbbr = useSessionStore(
    (s: SessionState) => s.selectedDestinationStateAbbr
  );
  const senderFirstName = useSessionStore(
    (s: SessionState) => (s as any).senderFirstName
  );
  const senderMiddleName = useSessionStore(
    (s: SessionState) => (s as any).senderMiddleName
  );
  const senderLastName = useSessionStore(
    (s: SessionState) => (s as any).senderLastName
  );
  const mapiSenderAddressValues = useSessionStore(
    (s: SessionState) => (s as any).mapiSenderAddressValues
  );
  const senderDOBString = useSessionStore(
    (s: SessionState) => (s as any).senderDOBString
  );
  const phoneNumber = useSessionStore(
    (s: SessionState) => (s as any).phoneNumber
  );
  const partnerName = useSessionStore(
    (s: SessionState) => (s as any).partnerName
  );
  const partnerAddress = useSessionStore(
    (s: SessionState) => (s as any).partnerAddress
  );
  const mapiSenderPrimaryIdentification = useSessionStore(
    (s: SessionState) => (s as any).mapiSenderPrimaryIdentification
  );
  const mapiSenderSecondaryIdentification = useSessionStore(
    (s: SessionState) => (s as any).mapiSenderSecondaryIdentification
  );
  const mapiReceiverDetails = useSessionStore(
    (s: SessionState) => (s as any).mapiReceiverDetails
  );

  const alpha2 = useMemo(() => {
    const a3 = (selectedDestinationCountryCode || "").toString().toUpperCase();
    return countries.alpha3ToAlpha2(a3) || "";
  }, [selectedDestinationCountryCode]);

  const countryCodeToEmoji = (code: string) => {
    if (!code) return "";
    const upper = code.toUpperCase();
    if (upper.length !== 2) return "";
    const base = 127397;
    return [...upper]
      .map((c) => String.fromCodePoint(base + c.charCodeAt(0)))
      .join("");
  };

  const senderName = useMemo(() => {
    return [senderFirstName, senderMiddleName, senderLastName]
      .filter(Boolean)
      .join(" ");
  }, [senderFirstName, senderMiddleName, senderLastName]);

  const senderAddressFull = useMemo(() => {
    const m = mapiSenderAddressValues || {};
    const parts = mapiSenderAddressFieldDisplayOrder
      .map((tag) => (typeof m[tag] === "string" ? m[tag].trim() : ""))
      .filter(Boolean);
    return parts.join(", ");
  }, [mapiSenderAddressValues]);

  const receiverName = useMemo(() => {
    if (!mapiReceiverDetails || typeof mapiReceiverDetails !== "object")
      return "";
    const first =
      mapiReceiverDetails["receiver.name.firstName"] ||
      mapiReceiverDetails["receiver.name.givenName"] ||
      "";
    const middle =
      mapiReceiverDetails["receiver.name.middleName"] || "";
    const last =
      mapiReceiverDetails["receiver.name.lastName"] ||
      mapiReceiverDetails["receiver.name.familyName"] ||
      "";
    const secondLast =
      mapiReceiverDetails["receiver.name.secondLastName"] || "";
    return [first, middle, last, secondLast].filter(Boolean).join(" ");
  }, [mapiReceiverDetails]);

  const receiverAddress = useMemo(() => {
    if (!mapiReceiverDetails || typeof mapiReceiverDetails !== "object")
      return "";
    const line1 = mapiReceiverDetails["receiver.address.line1"] || "";
    const line2 = mapiReceiverDetails["receiver.address.line2"] || "";
    const city = mapiReceiverDetails["receiver.address.city"] || "";
    const state = mapiReceiverDetails["receiver.address.countrySubdivisionCode"] || "";
    const postal = mapiReceiverDetails["receiver.address.postalCode"] || "";
    const country = mapiReceiverDetails["receiver.address.countryCode"] || "";
    return [line1, line2, [city, state, postal].filter(Boolean).join(", "), country]
      .filter(Boolean)
      .join("\n");
  }, [mapiReceiverDetails]);

  const primaryIdNumber = useMemo(() => {
    if (!mapiSenderPrimaryIdentification || typeof mapiSenderPrimaryIdentification !== "object")
      return "";
    return mapiSenderPrimaryIdentification["sender.primaryIdentification.id"] || "";
  }, [mapiSenderPrimaryIdentification]);

  const secondaryIdNumber = useMemo(() => {
    if (!mapiSenderSecondaryIdentification || typeof mapiSenderSecondaryIdentification !== "object")
      return "";
    return mapiSenderSecondaryIdentification["sender.secondaryIdentification.id"] || "";
  }, [mapiSenderSecondaryIdentification]);

  const sendAmount = pendingSendOption?.sendAmount?.amount?.value ?? 0;
  const sendCurrency =
    pendingSendOption?.sendAmount?.amount?.currencyCode || "USD";
  const receiveAmount = pendingSendOption?.receiveAmount?.amount?.value ?? 0;
  const receiveCurrency =
    pendingSendOption?.receiveAmount?.amount?.currencyCode || "USD";
  const fees = pendingSendOption?.sendAmount?.fees?.value ?? 0;
  const total =
    Number(sendAmount) + Number(pendingSendOption?.sendAmount?.fees?.value ?? 0);

  const formatPhoneNumber = (number: string) => {
    if (!number) return "";
    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const maskedIDNumber = useMemo(() => {
    const raw = String(primaryIdNumber || "");
    if (!raw) return "";
    const first2 = raw.slice(0, 2);
    const last2 = raw.slice(-2);
    const maskLength = Math.max(0, raw.length - 4);
    return `${first2}${"*".repeat(maskLength)}${last2}`;
  }, [primaryIdNumber]);

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>

      {partnerAddress && (
        <View>
          <Text style={styles.inputLabel}>{t("CVS_STORE_LOCATION")}</Text>
          <Text
            style={[
              styles.inputLabel,
              { fontSize: 16, lineHeight: 20, color: Colors.titleTextColor },
            ]}
          >
            {partnerAddress}
          </Text>
        </View>
      )}

      <View style={[styles.sectionView, { marginTop: 24 }]}>
        <View>
          <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>
              {Number(sendAmount).toFixed(2)}
            </Text>
            <Text style={styles.currencyText}>{sendCurrency}</Text>
          </View>
          <Text style={[styles.inputLabel, { marginTop: 8 }]}>
            {t("REVIEW_THEY_RECEIVE")}
          </Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>
              {Number(receiveAmount).toFixed(2)}
            </Text>
            <Text style={styles.currencyText}>{receiveCurrency}</Text>
          </View>
        </View>
        <TouchableOpacity
          testID="mapi-sendingAmountEntryButton"
          style={styles.editButton}
          onPress={() => onEditStep?.("amount")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionView}>
        <View>
          <View style={[styles.amountInput, { alignItems: "center" }]}>
            {Platform.OS === "web" ? (
              <View style={styles.flagWrapper}>
                {alpha2 ? (
                  <ReactCountryFlag
                    countryCode={alpha2}
                    svg
                    style={{ width: 30, height: 30, borderRadius: 50 }}
                    title={alpha2}
                  />
                ) : (
                  <Text style={{ fontSize: 16 }}>🌍</Text>
                )}
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>
                {countryCodeToEmoji(alpha2) || "🌍"}
              </Text>
            )}
            <View>
              <Text style={styles.inputLabel}>
                {t("REVIEW_DESTINATION_COUNTRY")}
              </Text>
              <Text style={styles.CountryText}>
                {selectedDestinationCountryName || selectedDestinationCountryCode || ""}
              </Text>
            </View>
          </View>
        </View>
        <TouchableOpacity
          testID="mapi-countryButton"
          style={styles.editButton}
          onPress={() => onEditStep?.("country")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {(selectedDestinationCountryCode === "USA" ||
        selectedDestinationCountryCode === "CAN") && (
        <View style={styles.sectionView}>
          <View>
            <Text style={styles.inputLabel}>{t("DESTINATION_STATE")}</Text>
            <View style={[styles.amountInput, { marginTop: 2, alignItems: "center" }]}>
              <Text style={styles.CountryText}>
                {selectedDestinationStateAbbr || ""}
              </Text>
            </View>
          </View>
          <TouchableOpacity
            testID="mapi-stateButton"
            style={styles.editButton}
            onPress={() => onEditStep?.("state")}
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.sectionView}>
        <View>
          <Text style={styles.inputLabel}>{t("RECEIVER_NAME")}</Text>
          <Text style={styles.pickUpText}>{receiverName || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="mapi-receiverNameButton"
          style={styles.editButton}
          onPress={() => onEditStep?.("receiverDetails")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {receiverAddress ? (
        <View style={styles.sectionView}>
          <View>
            <Text style={styles.inputLabel}>{t("RECEIVER_ADDRESS") || "Receiver Address"}</Text>
            <Text style={styles.pickUpText}>{receiverAddress}</Text>
          </View>
          <TouchableOpacity
            testID="mapi-receiverAddressButton"
            style={styles.editButton}
            onPress={() => onEditStep?.("receiverDetails")}
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("RECEIVE_METHOD_TITLE")}</Text>
          <Text
            style={[styles.pickUpText, { flexShrink: 1 }]}
            numberOfLines={2}
          >
            {pendingSendOption?.serviceOptionName ||
              pendingSendOption?.DeliveryOptDisplayName ||
              "—"}
          </Text>
        </View>
        <TouchableOpacity
          testID="mapi-howTheyReceiveButton"
          style={[styles.editButton, { marginLeft: 0 }]}
          onPress={() => onEditStep?.("howTheyReceive")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.yourInfoStyle}>{t("YOUR_INFO")}</Text>

      <View style={[styles.sectionView, { marginTop: 16 }]}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("SENDER_NAME")}</Text>
          <Text style={styles.pickUpText}>{senderName || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="mapi-senderNameButton"
          style={styles.editButton}
          onPress={() => onEditStep?.("senderName")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("SENDER_ADDRESS")}</Text>
          <Text style={styles.pickUpText}>{senderAddressFull || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="mapi-senderAddressButton"
          style={styles.editButton}
          onPress={() => onEditStep?.("senderAddress")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("PHONE_NUMBER")}</Text>
          <Text style={styles.pickUpText}>
            {formatPhoneNumber(phoneNumber || "") || "—"}
          </Text>
        </View>
        <TouchableOpacity
          testID="mapi-phoneButton"
          style={styles.editButton}
          onPress={() => onEditStep?.("phone")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {primaryIdNumber && (
        <View style={styles.sectionView}>
          <View style={styles.textContainer}>
            <Text style={styles.inputLabel}>{t("ID_NUMBER")}</Text>
            <Text style={styles.pickUpText}>{maskedIDNumber}</Text>
          </View>
          <TouchableOpacity
            testID="mapi-senderIdNumberButton"
            style={styles.editButton}
            onPress={() => onEditStep?.("senderID")}
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      )}

      {secondaryIdNumber && (
        <View style={styles.sectionView}>
          <View style={styles.textContainer}>
            <Text style={styles.inputLabel}>{t("SECONDARY_ID") || "Secondary ID"}</Text>
            <Text style={styles.pickUpText}>
              {secondaryIdNumber.length > 4
                ? `${secondaryIdNumber.slice(0, 2)}${"*".repeat(secondaryIdNumber.length - 4)}${secondaryIdNumber.slice(-2)}`
                : "—"}
            </Text>
          </View>
          <TouchableOpacity
            testID="mapi-senderId2Button"
            style={styles.editButton}
            onPress={() => onEditStep?.("senderID2")}
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={[styles.sectionView, { marginBottom: 32 }]}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("DOB")}</Text>
          <Text style={styles.pickUpText}>{senderDOBString || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="mapi-senderDOBButton"
          style={styles.editButton}
          onPress={() => onEditStep?.("senderDOB")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={{ marginBottom: 16 }}>
        {i18n.language === "en" ? (
          <View>
            <Text style={styles.terms}>
              I agree to MoneyGram's{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Terms and Conditions
              </Text>{" "}
              and confirm I am the sole beneficial owner of the assets, and am
              acting solely on my own profile. By continuing with this
              transaction I consent to the collection, use, disclosure, and
              transfer (including cross-border transfer) of my personal
              information as described in our{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Privacy Policy.
              </Text>
            </Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              <Text style={styles.bold}>Exchange rate</Text>
              {"\n"}
              In addition to transfer fees, a currency exchange rate, also
              determined by MoneyGram, may also apply. MoneyGram may also make
              money from currency exchange. When selecting a money transfer
              operator, carefully compare both transfer fees and currency
              exchange rates.
            </Text>
          </View>
        ) : (
          <View>
            <Text style={styles.terms}>{t("TERMS_AND_CONDITIONS")}</Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              {t("TERMS_EXCHANGE_POLICY")}
            </Text>
          </View>
        )}
      </View>

    </View>
  );
};

export default MapiReviewFinalStep;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
    justifyContent: "flex-start",
    paddingBottom: 40,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "400",
    fontFamily: "Rubik",
    color: Colors.subTitleColor,
    lineHeight: 22,
    alignSelf: "stretch",
  },
  amountInput: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  pickUpText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  yourInfoStyle: {
    fontFamily: "Rubik",
    fontSize: 20,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 26,
    marginTop: 32,
  },
  textContainer: {
    flex: 1,
    flexShrink: 1,
    marginRight: 8,
  },
  amountText: {
    fontFamily: "Rubik",
    fontSize: 32,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 30,
  },
  sectionView: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 32,
  },
  currencyText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 24,
    paddingLeft: 8,
  },
  editButton: {
    backgroundColor: Colors.searchBackground,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  editText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 16,
  },
  CountryText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 24,
  },
  flagWrapper: {
    width: 20,
    height: 20,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  terms: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  link: {
    color: Colors.titleTextColor,
    fontWeight: "500",
  },
  bold: { fontWeight: "600" },
  continueButton: {
    alignSelf: "stretch",
    backgroundColor: Colors.black,
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    marginTop: 24,
  },
  continueButtonText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
    lineHeight: 24,
  },
});
